import {
  Component,
  Input,
  OnChanges,
  OnInit,
  SimpleChanges,
} from '@angular/core';
import { Layer, marker, PointTuple, polygon, polyline } from 'leaflet';
import { LeafletModule } from '@bluehalo/ngx-leaflet';
import { MapComponent } from '../map/map.component';
import { getMapIcon } from '../../utils/map-utils';
import { MapDevice } from '../../models/map-device.interface';
import { IconComponent } from '../../../../shared/icons/icon.component';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
  selector: 'app-map-with-location',
  imports: [LeafletModule, MapComponent, IconComponent, TranslatePipe],
  templateUrl: './map-with-location.component.html',
  styleUrl: './map-with-location.component.scss',
})
export class MapWithLocationComponent implements OnInit, OnChanges {
  @Input() device?: MapDevice;
  @Input() devices?: MapDevice[] | null;
  @Input() groupKey?: string;
  @Input() navigateToMaps: boolean = false;

  layers: Layer[] = [];

  getCoord(d: MapDevice): PointTuple | null {
    if (d.latitude != null && d.longitude != null) {
      return [d.latitude, d.longitude];
    }
    return null;
  }

  ngOnInit() {
    this.refreshLayers();
  }

  onOpenInMaps(lat: any, lon: any): void {
    const url = `https://www.google.com/maps?q=${lat},${lon}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  }

  ngOnChanges(changes: SimpleChanges) {
    if (!changes['device'] && !changes['devices'] && !changes['groupKey'])
      return;
    this.refreshLayers();
  }

  private refreshLayers() {
    this.layers = [];
    const items = this.devices?.length
      ? this.devices
      : this.device
        ? [this.device]
        : [];
    if (items.length === 0) return;

    const lineLayers: Layer[] = [];
    const markerLayers: Layer[] = [];
    const coords: PointTuple[] = [];
    for (const item of items) {
      const coord = this.getCoord(item);
      if (!coord) continue;
      coords.push(coord);

      markerLayers.push(
        marker(coord, {
          icon: getMapIcon(item.type, this.getMarkerStatus(item)),
        }),
      );
    }

    const uniqueCoords = coords.filter(
      (coord, index, array) =>
        array.findIndex(
          ([lat, lon]) => lat === coord[0] && lon === coord[1],
        ) === index,
    );

    const groupColor = this.getGroupColor(this.groupKey ?? 'default');

    if (uniqueCoords.length === 2) {
      const [start, end] = uniqueCoords;
      const twoPointPolygon = this.buildTwoPointPolygon(start, end);
      lineLayers.push(
        polygon(twoPointPolygon, {
          color: groupColor,
          weight: 3,
          opacity: 0.9,
          fillColor: groupColor,
          fillOpacity: 0.2,
          pane: 'connectionsPane',
        }),
      );
    } else if (uniqueCoords.length > 2) {
      const hull = this.expandPolygon(this.getConvexHull(uniqueCoords));
      lineLayers.push(
        polygon(hull, {
          color: groupColor,
          weight: 3,
          opacity: 0.9,
          fillColor: groupColor,
          fillOpacity: 0.2,
          pane: 'connectionsPane',
        }),
      );
    }

    this.layers = [...lineLayers, ...markerLayers];
  }

  private getMarkerStatus(
    device: MapDevice,
  ): 'default' | 'favorite' | 'outOfService' {
    if (device.isOutOfService) {
      return 'outOfService';
    }

    if (device.isFavorite) {
      return 'favorite';
    }

    return 'default';
  }

  private buildTwoPointPolygon(
    start: PointTuple,
    end: PointTuple,
  ): PointTuple[] {
    const [lat1, lon1] = start;
    const [lat2, lon2] = end;
    const dLat = lat2 - lat1;
    const dLon = lon2 - lon1;
    const distance = Math.hypot(dLat, dLon);

    if (distance === 0) {
      return [start, end, end, start];
    }

    const perpLat = -dLon / distance;
    const perpLon = dLat / distance;
    const halfWidth = Math.max(distance * 0.008, 0.00025);

    const p1: PointTuple = [
      lat1 + perpLat * halfWidth,
      lon1 + perpLon * halfWidth,
    ];
    const p2: PointTuple = [
      lat2 + perpLat * halfWidth,
      lon2 + perpLon * halfWidth,
    ];
    const p3: PointTuple = [
      lat2 - perpLat * halfWidth,
      lon2 - perpLon * halfWidth,
    ];
    const p4: PointTuple = [
      lat1 - perpLat * halfWidth,
      lon1 - perpLon * halfWidth,
    ];

    return [p1, p2, p3, p4];
  }

  private getConvexHull(points: PointTuple[]): PointTuple[] {
    if (points.length <= 3) {
      return points;
    }

    const sorted = [...points].sort((a, b) => a[1] - b[1] || a[0] - b[0]);

    const cross = (o: PointTuple, a: PointTuple, b: PointTuple): number =>
      (a[1] - o[1]) * (b[0] - o[0]) - (a[0] - o[0]) * (b[1] - o[1]);

    const lower: PointTuple[] = [];
    for (const p of sorted) {
      while (
        lower.length >= 2 &&
        cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0
      ) {
        lower.pop();
      }
      lower.push(p);
    }

    const upper: PointTuple[] = [];
    for (let i = sorted.length - 1; i >= 0; i--) {
      const p = sorted[i];
      while (
        upper.length >= 2 &&
        cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0
      ) {
        upper.pop();
      }
      upper.push(p);
    }

    lower.pop();
    upper.pop();
    return [...lower, ...upper];
  }

  private expandPolygon(points: PointTuple[]): PointTuple[] {
    if (points.length < 3) {
      return points;
    }

    const centroidLat =
      points.reduce((sum, [lat]) => sum + lat, 0) / points.length;
    const centroidLon =
      points.reduce((sum, [, lon]) => sum + lon, 0) / points.length;
    const scale = 1.12;
    const minOffset = 0.0002;

    return points.map(([lat, lon]) => {
      const dLat = lat - centroidLat;
      const dLon = lon - centroidLon;
      const distance = Math.hypot(dLat, dLon);
      if (distance === 0) {
        return [lat, lon] as PointTuple;
      }

      const targetDistance = Math.max(distance * scale, distance + minOffset);
      const factor = targetDistance / distance;
      return [centroidLat + dLat * factor, centroidLon + dLon * factor];
    });
  }

  private getGroupColor(key: string): string {
    const palette = [
      '#0d47a1',
      '#00695c',
      '#c62828',
      '#6a1b9a',
      '#ef6c00',
      '#2e7d32',
      '#5d4037',
      '#1565c0',
    ];
    const hash = Array.from(key).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return palette[hash % palette.length];
  }
}
